import React, {
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
  memo
} from 'react';
import classNames from 'classnames';

import { EMPTY_COMPONENT } from 'common/constants';

import { Polyline } from 'entities/map';
import { NavigationHistoryPath } from 'entities/navigation';
import { FitBounds } from 'common/types/map/fitBounds';
import { MapPadding } from 'common/utils/Map/types';
import { IStation } from 'dataSources/Typicode/stations/types';
import { RouteSettings } from 'common/utils/Route/types/routeSettings';
import { RoutePoints } from 'common/utils/directionsService';
import { Route, Routes } from 'common/utils/Route/types/routes';
import { Params as RouteConstructorParams } from 'common/utils/Route/types/params';
import { OnMarkerClick } from './hooks/useOnStationMarkerClick';

import Controls from './Controls';
import Tooltip from './Tooltip';
import Footer from './Footer';

import useMarkers from './hooks/useMarkers';
import useFitBounds from './hooks/useFitBounds';
import useOnMapClick from './hooks/useOnMapClick';
import useFocusPoint from './hooks/useFocusPoint';
import useMapStyles from './hooks/useMapStyles';
import useGlobalStyles from './useGlobalStyles';
import {
  useInitMap,
  useMapZoomObserver,
  useNavigationHistoryRoutes,
  useTravelDirectionMarker
} from './hooks';
import { useIsActiveGoogleMap } from 'entities/application';

import withContext from './hoc/withContext';
import { useMapContext } from './context/map';
import { useMarkersContext } from './context'

import { Map as MapInterface } from 'common/utils/Map';
import { Marker } from 'common/utils/MapMarker/types';
import { FocusPoint } from './types/focusPoint';
import { FooterElements } from './types/footerElements';
import { Layers } from 'common/utils/MapMarker/types';

// Routes
import { onChangeRoutes } from './types/onChangeRoutes';

import useStyles from './useStyles';
import { useShowTollPolyline } from './hooks/useShowTollPolyline';
export interface Props {
  onMarkerClick?: OnMarkerClick;
  stations?: IStation[];
  extraStations?: IStation[];
  fitBounds?: FitBounds;
  fitBoundsPadding?: MapPadding;
  showZoomControl?: boolean;
  setIsStreetView?: Dispatch<SetStateAction<boolean>>;
  isStreetView?: boolean;
  onInitializedMap?: () => void;
  focusPoint?: FocusPoint;
  showMapTypeControl?: boolean;
  routes?: Routes;
  activeRoute?: Route;
  routesPoints?: RoutePoints;
  routeInitData?: RouteConstructorParams;
  onChangeRoutes?: onChangeRoutes;
  routeModifiers?: RouteSettings;
  styles?: React.CSSProperties;
  footerElements?: FooterElements;
  showStationsDisplayButton?: boolean;
  navigationHistoryPath?: NavigationHistoryPath[];
  navigationHistoryRoutes?: Polyline[];
  onChangeNavigationHistoryRoutes?: (value?: Polyline[]) => void;
  carHistoryTravelPointIndex?: number;
  layers?: Layers;
  renderElements?: (props: {
    map: MapInterface | null,
    markers: Marker | null
  }) => JSX.Element | null;
  onUpdateMap?: (map: MapInterface | null) => void;
  showLocationControl?: boolean;
};

const Map = ({
  onMarkerClick,
  fitBounds,
  fitBoundsPadding,
  stations,
  extraStations,
  showZoomControl = true,
  styles,
  isStreetView = false,
  setIsStreetView,
  focusPoint,
  showMapTypeControl = true,
  onInitializedMap,
  footerElements,
  showStationsDisplayButton = false,
  navigationHistoryPath,
  navigationHistoryRoutes,
  onChangeNavigationHistoryRoutes,
  renderElements = EMPTY_COMPONENT,
  carHistoryTravelPointIndex,
  onUpdateMap,
  showLocationControl
}: Props) => {
  const classes = useStyles({ isStreetView });
  const containerRef = useRef<HTMLDivElement>(null);
  const mapStyles = useMapStyles(styles);
  const isActiveGoogleMap = useIsActiveGoogleMap();

  useShowTollPolyline();
  useTravelDirectionMarker(carHistoryTravelPointIndex)
  useMapZoomObserver();
  useOnMapClick();
  useInitMap(containerRef, onInitializedMap);
  useMarkers({
    stations,
    extraStations,
  }, {
    onMarkerClick,
  });
  useFitBounds(fitBounds, fitBoundsPadding);
  useFocusPoint(focusPoint);
  useGlobalStyles();

  const {
    addListenerStreetViewChange,
    map
  } = useMapContext();
  const { markers } = useMarkersContext();
  
  useNavigationHistoryRoutes(
    navigationHistoryPath,
    navigationHistoryRoutes,
    onChangeNavigationHistoryRoutes
  );

  useEffect(() => {
    if (isActiveGoogleMap) {
      addListenerStreetViewChange(setIsStreetView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isActiveGoogleMap]);

  useEffect(() => {
    if (onUpdateMap) {
      onUpdateMap(map);
    }
  }, [
    onUpdateMap,
    map
  ]);

  return (
    <>
      <Controls
        showZoomControl={showZoomControl}
        showLocationControl={showLocationControl}
      />
      <Tooltip
        containerRef={containerRef}
      />
      <Footer
        showMapTypeControl={showMapTypeControl}
        footerElements={footerElements}
        showStationsDisplayButton={showStationsDisplayButton}
      />
      <div
        id="map"
        ref={containerRef}
        style={mapStyles}
        className={classNames({
          [classes.map]: true,
          [classes.gmStyle]: showZoomControl
        })}
        onClick={(event: any) => {
          event.stopPropagation();
        }}
      />
      {renderElements({ map, markers })}
    </>
  );
};

export default memo(withContext(Map));
